import React, { useEffect, useRef, useState, useMemo, useContext } from "react";
import { useSelector } from "react-redux";
import { gsap } from "gsap";
import { useNavigate } from "react-router-dom";
import { usePostHog } from 'posthog-js/react';
import { useForm, Controller } from "react-hook-form";

import { Stack, Alert, Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import CustomInput from "./Custom/CustomInput";
import CustomTextArea from "./Custom/CustomTextArea";
import UncontrolledAutocomplete from "./Custom/UncontrolledAutocomplete";
import AsyncProjectAutocomplete from "./Custom/AsyncProjectAutocomplete";
import CustomDatePicker from "./Custom/CustomDatePicker";
import CustomButton from "./Custom/CustomButton";
import CustomUpload from "./Custom/CustomUpload";
import AsyncLabelAutocomplete from "./Custom/AsyncLabelAutocomplete";
import "./ModalStyles.css";
import { NotificationsContext } from "../context/NotificationsContext";
import axiosInstance from "../api/axiosInstance";
import getApiUrl from "../utils/api";
import { toast } from "react-toastify";
import { POSTHOG_EVENTS } from "../constants/posthog-events";
import {
  buildEmployeeAssigneeOption,
  filterEmployeeAssigneeOptions,
  renderEmployeeAssigneeOption,
} from "./common/EmployeeAssigneeOption";

const AddTaskModal = ({ open, onClose, onTaskCreated }) => {
  const posthog = usePostHog();
  const overlayRef = useRef(null);
  const contentRef = useRef(null);

  const { addNotification } = useContext(NotificationsContext);

  const employees = useSelector((state) => state.employees.employees) || [];
  const allProjects = useSelector((state) => state.projects.allProjects) || [];
  const rawGroups = useSelector((state) => state.groups?.allGroups || []);
  const allGroups = Array.isArray(rawGroups) ? rawGroups : [];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const authUser = useSelector((state) => state.auth.user);
  const currentUserId = authUser?.userId || authUser?.user_id || authUser?.id || authUser?.sub;
  const navigate = useNavigate();

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      title: "",
      priority: null,
      project: null,
      startDate: null,
      targetDate: null,
      description: "",
      assignType: "members",
      selectionMode: "multi",
      assignees: [],
      selectedGroup: null,
      labels: [],
      attachments: [],
    },
    shouldFocusError: true,
  });

  const assignType = watch("assignType");
  const currentAssignees = watch("assignees");
  const startDateValue = watch("startDate");
  const targetDateValue = watch("targetDate");

  const isMeSelected = useMemo(() => {
    if (!currentUserId || !Array.isArray(currentAssignees)) return false;
    return currentAssignees.some(a => String(a.value) === String(currentUserId));
  }, [currentAssignees, currentUserId]);

  // ── Derived options ────────────────────────────────────────────────────────
  const projectOptions = useMemo(
    () =>
      allProjects.map((p) => ({
        value: String(p.id || p.Id || p.projectId || ""),
        label: p.name || p.projectName || p.project_name || p.project || `Project #${p.id || p.Id || "?"}`,
      })),
    [allProjects]
  );

  const allGroupOptions = useMemo(
    () =>
      allGroups.map((g) => ({
        value: String(g.id),
        label: g.groupName || g.name || `Group #${g.id}`,
      })),
    [allGroups]
  );

  const employeeOptions = useMemo(
    () => employees.map(buildEmployeeAssigneeOption),
    [employees]
  );

  const priorityOptions = useMemo(
    () => [
      { value: "urgent", label: "Urgent" },
      { value: "high", label: "High" },
      { value: "medium", label: "Medium" },
      { value: "low", label: "Low" },
    ],
    []
  );

  const hasUserGroups = allGroupOptions.length > 0;

  // ── Modal open / close animation ──────────────────────────────────────────
  useEffect(() => {
    if (open) {
      if (overlayRef.current) overlayRef.current.style.display = "flex";
      if (contentRef.current) {
        gsap.fromTo(
          contentRef.current,
          { y: 120, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.38, ease: "power3.out" }
        );
      }

      // Pre-fill project from active project context
      const activeProjectTitle = localStorage.getItem("activeProjectTitle");
      if (activeProjectTitle && projectOptions.length > 0) {
        const match = projectOptions.find(
          (opt) => opt.label.toLowerCase() === activeProjectTitle.toLowerCase()
        );
        if (match) setValue("project", match);
      }
    } else {
      reset();
      setError("");
    }
  }, [open, projectOptions, reset, setValue]);

  const handleClose = () => {
    if (contentRef.current) {
      gsap.to(contentRef.current, {
        y: 120,
        opacity: 0,
        duration: 0.22,
        ease: "power2.in",
        onComplete: () => {
          if (overlayRef.current) overlayRef.current.style.display = "none";
          onClose();
        },
      });
    } else {
      if (overlayRef.current) overlayRef.current.style.display = "none";
      onClose();
    }
  };

  // ── Form submission ────────────────────────────────────────────────────────
  const onSubmitForm = async (data) => {
    setError("");

    // Client-side assignee validation (react-hook-form rules don't cover
    // the group vs members logic, so we guard it here).
    if (data.assignType === "group") {
      if (!data.selectedGroup) {
        const msg = "Please select a group to assign this task to.";
        setError(msg);
        toast.error(msg);
        return;
      }
    } else {
      const hasAssignees = Array.isArray(data.assignees) && data.assignees.length > 0;

      if (!hasAssignees) {
        const msg = "Please choose at least one assignee.";
        setError(msg);
        toast.error(msg);
        return;
      }
    }

    setLoading(true);

    try {
      // ── Build FormData payload matching POST /api/task/createTask ──────────
      // Required : title, (assignees XOR group_id), priority, status, due_date
      // Optional : description, start_date, project_id, taskAttachments[]
      const formData = new FormData();
      formData.append("title", data.title.trim());
      formData.append("description", data.description?.trim() || "");
      formData.append("priority", data.priority.value);
      formData.append("status", "pending");
      formData.append("due_date", data.targetDate.format("YYYY-MM-DD"));

      if (data.startDate) {
        formData.append("start_date", data.startDate.format("YYYY-MM-DD"));
      }

      if (data.project) {
        formData.append("project_id", data.project.value);
      }

      if (data.labels && data.labels.length > 0) {
        formData.append("labels", JSON.stringify(data.labels.map(l => l.value)));
      }

      // Assignee: group_id XOR assignees (single UUID or JSON-encoded array)
      if (data.assignType === "group") {
        formData.append("group_id", data.selectedGroup.value);
      } else {
        // Backend sanitiser accepts a JSON string for multipart arrays
        formData.append(
          "assignees",
          JSON.stringify(data.assignees.map((e) => e.value))
        );
      }

      // Attachments go in the same request — no separate upload call needed
      if (data.attachments?.length > 0) {
        data.attachments.forEach((file) => {
          formData.append("taskAttachments", file);
        });
      }

      // axiosInstance handles auth (httpOnly cookie + interceptors)
      const response = await axiosInstance.post(
        getApiUrl("api/task/createTask"),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // Response envelope: { success, statusCode, message, data: <TaskDetail> }
      const createdTask = response.data?.data ?? response.data;

      const createdById = currentUserId || null;
      const taskId = createdTask?.id || null;
      const assigneeIds =
        data.assignType === "members"
          ? data.assignees.map((e) => e.value)
          : [];

      posthog?.capture(POSTHOG_EVENTS.TASK_CREATED, {
        task_id: taskId,
        title: data.title.trim(),
        priority: data.priority?.value || null,
        status: "pending",
        project_id: data.project?.value || null,
        assign_type: data.assignType,
        group_id: data.assignType === "group" ? data.selectedGroup?.value : null,
        assignee_count:
          data.assignType === "members" ? assigneeIds.length : null,
        assignee_ids:
          data.assignType === "members" && assigneeIds.length > 0
            ? assigneeIds.map(String)
            : null,
        start_date: data.startDate ? data.startDate.format("YYYY-MM-DD") : null,
        due_date: data.targetDate ? data.targetDate.format("YYYY-MM-DD") : null,
        attachments_count: data.attachments?.length || 0,
        has_description: Boolean(data.description?.trim()),
        created_by: createdById,
      });

      toast.success(response.data?.message || "Task created successfully!");

      if (onTaskCreated) onTaskCreated(createdTask);

      // In-app notification for direct assignment (first assignee if multiple)
      if (data.assignType === "members" && data.assignees.length > 0) {
        data.assignees.forEach(assignee => {
          addNotification({
            type: "task_assigned",
            message: `You have been assigned a new task: ${data.title}`,
            recipientId: assignee.value,
            isRead: false,
            link: `/task/${createdTask?.id}`,
          });
        });
      }

      handleClose();
    } catch (err) {
      // 401 → already redirected to /sign-in by the axiosInstance interceptor.
      // 403 → insufficient permissions.
      const status = err.status ?? err.originalError?.response?.status;
      if (status === 403) {
        navigate("/login");
        return;
      }

      // express-validator validation errors arrive as an array under data.errors
      const validationErrors = err.data?.errors;
      const errorMsg = validationErrors?.length
        ? validationErrors.map((e) => e.msg).join("; ")
        : err.message || "Failed to create task.";

      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const LISTBOX_PROPS = {
    listbox: { style: { fontSize: "12px" } },
    paper: { sx: { fontSize: "12px" } },
  };

  return (
    <div
      ref={overlayRef}
      className="modal-overlay-base"
      style={{ display: "none" }}
    >
      <div
        ref={contentRef}
        className="modal-content-base"
        style={{ maxWidth: 700 }}
      >
        <div className="modal-header-base">
          <h3 className="modal-title-base">Add Task</h3>
          <button className="modal-close-base" onClick={handleClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="modal-body-base">
          <form id="add-task-form" onSubmit={handleSubmit(onSubmitForm)}>
            <Stack spacing={3} sx={{ width: "100%" }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{ fontSize: "0.82rem", borderRadius: "8px", mb: 2 }}
                >
                  {error}
                </Alert>
              )}
              {/* Title */}
              <CustomInput
                control={control}
                name="title"
                label="Task Title *"
                rules={{ required: "Task Title is required" }}
                placeholder="Enter Task Title"
              />

              {/* Priority + Project */}
              <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Controller
                    name="priority"
                    control={control}
                    rules={{ required: "Priority is required" }}
                    render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                      <UncontrolledAutocomplete
                        {...field}
                        ref={ref}
                        label="Priority *"
                        options={priorityOptions}
                        error={e?.message}
                        idx="priority-select"
                        fullWidth
                      />
                    )}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Controller
                    name="project"
                    control={control}
                    render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                      <AsyncProjectAutocomplete
                        {...field}
                        ref={ref}
                        label="Project"
                        error={e?.message}
                      />
                    )}
                  />
                </Box>
              </Stack>

              {/* Dates */}
              <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomDatePicker
                    control={control}
                    name="startDate"
                    label="Start Date *"
                    maxDate={targetDateValue}
                    rules={{ required: "Start Date is required" }}
                    clearable={true}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomDatePicker
                    control={control}
                    name="targetDate"
                    label="Target End Date *"
                    rules={{ required: "Target End Date is required" }}
                    minDate={startDateValue}
                    clearable={true}
                  />
                </Box>
              </Stack>

              {/* Assign type toggle */}
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  alignItems: "center",
                  background: "#f8fafc",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#475569",
                    marginRight: "8px",
                  }}
                >
                  Assign To:
                </span>
                {["group", "members"].map((type) => (
                  <label
                    key={type}
                    style={{
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      color: "#1e293b",
                    }}
                  >
                    <input
                      type="radio"
                      name="assignType"
                      checked={assignType === type}
                      onChange={() => setValue("assignType", type)}
                      style={{ cursor: "pointer", accentColor: "#2563eb" }}
                    />
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </label>
                ))}
              </div>

              {/* Assignee selector */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}
                  >
                    {assignType === "group"
                      ? "Choose Group"
                      : "Choose Employees"}{" "}
                    *
                  </span>

                  {assignType === "members" && (
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      {currentUserId && (
                        <button
                          type="button"
                          onClick={() => {
                            const me = employeeOptions.find(opt => String(opt.value) === String(currentUserId));
                            if (!me) {
                              toast.warning("Current user not found in employee list");
                              return;
                            }

                            const list = Array.isArray(currentAssignees) ? currentAssignees : [];
                            if (isMeSelected) {
                              setValue("assignees", list.filter(a => String(a.value) !== String(currentUserId)));
                            } else {
                              setValue("assignees", [...list, me]);
                            }
                          }}
                          style={{
                            background: isMeSelected ? "rgba(37, 99, 235, 0.08)" : "none",
                            border: "1px solid",
                            borderColor: isMeSelected ? "rgba(37, 99, 235, 0.3)" : "transparent",
                            color: "#2563eb",
                            fontSize: "11px",
                            cursor: "pointer",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontWeight: 600,
                            transition: "all 0.2s ease"
                          }}
                        >
                          <PersonIcon sx={{ fontSize: "14px" }} />
                          {isMeSelected ? "Unassign me" : "Assign to me"}
                        </button>
                      )}


                    </div>
                  )}
                </div>

                {assignType === "group" ? (
                  <Controller
                    name="selectedGroup"
                    control={control}
                    render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                      <UncontrolledAutocomplete
                        {...field}
                        ref={ref}
                        label="Select Group"
                        options={allGroupOptions}
                        error={e?.message}
                        disabled={!hasUserGroups}
                        placeholder={
                          !hasUserGroups
                            ? "No groups available"
                            : "Select or search group..."
                        }
                        idx="group-select"
                        fullWidth
                      />
                    )}
                  />
                ) : (
                  <Controller
                    name="assignees"
                    control={control}
                    render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                      <UncontrolledAutocomplete
                        {...field}
                        ref={ref}
                        label="Assign Employee(s)"
                        options={employeeOptions}
                        multiple={true}
                        disableCloseOnSelect={true}
                        error={e?.message}
                        getOptionLabel={(opt) => opt?.name ?? opt?.label ?? ""}
                        renderOption={renderEmployeeAssigneeOption}
                        filterOptions={filterEmployeeAssigneeOptions}
                        slotProps={LISTBOX_PROPS}
                        isOptionEqualToValue={(opt, val) =>
                          opt.value === val.value
                        }
                        idx="assignee-select"
                        fullWidth
                      />
                    )}
                  />
                )}
              </div>

              {/* Description */}
              <CustomTextArea
                control={control}
                name="description"
                label="Description"
                placeholder="Add Description"
              />

              {/* Labels */}
              <Box sx={{ width: "100%" }}>
                <Controller
                  name="labels"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState: { error: e } }) => (
                    <AsyncLabelAutocomplete
                      {...field}
                      ref={ref}
                      label="Labels"
                      placeholder="Select or create labels"
                      error={e?.message}
                    />
                  )}
                />
              </Box>

              {/* Attachments */}
              <div className="custom-file-upload-section">
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}
                >
                  Attachments
                </Typography>
                <CustomUpload
                  control={control}
                  name="attachments"
                  isMultiple={true}
                  extensions={[
                    "jpg", "jpeg", "png", "pdf", "doc", "docx", "xls", "xlsx", "csv", "zip",
                  ]}
                  labelSize="small"
                  size="small"
                  maxFileSize={15728640}
                />
              </div>

            </Stack>
          </form>
        </div>

        <div
          className="modal-footer-base"
          style={{ justifyContent: "flex-end", gap: 12 }}
        >
          <CustomButton
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: 2, minWidth: 100 }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            type="submit"
            form="add-task-form"
            variant="contained"
            loading={loading}
            loadingText="Creating..."
            startIcon={<AddIcon />}
            className="modal-btn-submit"
          >
            Add Task
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;
